'use strict';

// Edge and Safari do not currently support ResizeObserver and so we need this import to get the
// desired window positioning behavior
import { install } from 'resize-observer';

/**
 * Tracks window scroll position so that it can be recalled and repositioned after page reloads (i.e. refresh button
 * clicks, back button clicks, redirects).
 *
 * How it works ...
 *
 * Window scroll position is mostly controlled within this component. Since there's no way in React to tell when the
 * last component on the screen has finished rendering (potentially changing the height of the window) this component
 * works by persisting the current scroll position using URL persistence, and positioning the scroll bar on page load
 * by observing changes to the outer application DOM element.
 *
 * WindowPositionStore registers a listener for scroll events and stores current window height and scroll position values
 * as they change. If they change significantly the data will be persisted using URL persistence.
 *
 * AppStore instantiates WindowPositionStore and calls its mount() method in its mount method. If WPS finds data it
 * registers an element resize observer for the app DOM element. When the scroll bar gets positioned the observer is
 * unregistered (because it should only happen once).
 */
class WindowPositionStore {

    /**
     * Constructor.
     */
    constructor(urlPersistenceStore) {
        this.isDebug = false;
        this.componentKey = "wps";
        this.reactAppId = "react_app";

        this.data = this.defaultData();

        this.resizeObserver;

        // Bind this for the scroll callback.
        this.handleScroll = this.handleScroll.bind(this);
        // Bind the resize observer.
        this.onHeightChange = this.onHeightChange.bind(this);

        // Register in the URL persistence store.
        this.urlPersistenceStore = urlPersistenceStore;
        this.urlPersistenceStore.register(this.componentKey, this.defaultData);
    }

    /**
     * Make the magic happen.
     */
    mount() {
        // Register a listener for scroll events.
        this._registerScrollEventListener();

        // Look for data in URL persistence.
        const data = this.urlPersistenceStore.getPersistedData(this.componentKey);
        if (data != null) {
            // Use persisted data if available.
            this.data = data;
            if (this.isDebug) {
                console.log(`WindowPositionStore: Initializing window position with data object [${JSON.stringify(data)}]`);
            }
            this.data.windowPosition = data.windowPosition;
            this.data.windowHeight = data.windowHeight;
            if (this.data.windowHeight > 0) {
                this._registerResizeObserver();
            }
        }
    }

    /**
     * The default data object.
     * @return {{windowHeight: number, windowPosition: number}}
     */
    defaultData() {
        return {
            windowHeight: 0,
            windowPosition: 0
        }
    }

    /**
     * Cease the Magic. Clean up.
     */
    unmount() {
        // Remove the scroll event.
        this._unRegisterScrollEventListener();
        this._removeResizeObserver();
    }

    /**
     * Set debugging.
     * @param isDebug True to turn on debugging. False to turn it off.
     */
    setDebug(isDebug) {
        this.isDebug = isDebug;
    }

    /**
     * Register an event listener for scroll events.
     * @private
     */
    _registerScrollEventListener() {
        if (this.isDebug) {
            console.log("WindowPositionStore: Registering scroll event listener.");
        }
        document.addEventListener("scroll", this.handleScroll);
    }

    _unRegisterScrollEventListener() {
        if (this.isDebug) {
            console.log("WindowPositionStore: Removing scroll event listener.");
        }
        document.removeEventListener("scroll", this.handleScroll);
    }

    /**
     * Starts the 'react_app' resize observer.
     * @private
     */
    _registerResizeObserver() {
        if (this.isDebug) {
            console.log(`WindowPositionStore: Registering '${this.reactAppId}' resize observer.`);
        }
        let reactApp = document.getElementById(this.reactAppId);

        // only install resize-observer when it doesn't already exist
        if (!window.ResizeObserver) install();
        this.resizeObserver = new ResizeObserver(this.onHeightChange);
        this.resizeObserver.observe(reactApp);
    }

    /**
     * Stops the 'react_app' resize observer.
     * @private
     */
    _removeResizeObserver() {
        if (this.isDebug) {
            console.log(`WindowPositionStore: Removing '${this.reactAppId}' resize observer.`);
        }
        let reactApp = document.getElementById(this.reactAppId);
        this.resizeObserver.unobserve(reactApp);
    }

    /**
     * App height change call back.
     * @param entries A list of element data. We only care about the first one since the element is unique.
     */
    onHeightChange = (entries) => {
        const entry = entries[0];
        this._positionScrollbar(entry.contentRect);
    };

    /**
     * Positions the scroll bar in the window.
     */
    _positionScrollbar(elementData) {
        /*
         * Decide whether or not to move the scroll bar. This is currently a pretty simple analysis ... If the persisted
         * window position is less than the height of the app dom element then don't scroll.
         */
        if (elementData.height > this.data.windowPosition) {
            if (this.isDebug) {
                console.log(`WindowPositionStore: Applying height change in [${JSON.stringify(elementData)}]. Scrolling to [${this.data.windowPosition}].`);
            }
            // No need to process scroll events when positioning the scroll bar.
            this._unRegisterScrollEventListener();
            window.scrollTo(0, this.data.windowPosition);
            // Resume listening for scroll events.
            this._registerScrollEventListener();
            // No need to observe size changes any more.
            this._removeResizeObserver();
        }
    }

    /**
     * The callback method for the scroll events.
     */
    handleScroll() {
        this._updateWindowParams(document.documentElement.scrollHeight, document.documentElement.scrollTop);
    }

    /**
     * Writes current scroll position into URL persistence.
     *
     * @param windowHeight Total height of the whole page.
     * @param windowPosition Position of the page in window.
     */
    _updateWindowParams(windowHeight, windowPosition) {
        // Only update if the position changes by a significant amount. Otherwise, it's too chatty.
        if (Math.max(this.data.windowPosition, windowPosition) - Math.min(this.data.windowPosition, windowPosition) > 250) {
            // Store the values.
            this.data.windowHeight = windowHeight;
            this.data.windowPosition = windowPosition;
            if (this.isDebug) {
                console.log(`WindowPositionStore: Pushing data [${this.componentKey}: ${JSON.stringify(this.data)}] to URL persistence.`);
            }
            this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data) ;
        }
    }
}

export default WindowPositionStore;